import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { add, addMinutes, differenceInSeconds, sub } from 'date-fns';
import { ChartDataSet } from '../../../common/Chart/Chart';
import { generateRandomChartColor } from '../../../common/Chart/LegendColor';
import { default_data_formatter, MetricType } from '../../../common/metrics/metric-definitions';
import MetricChartBase from '../../../common/metrics/MetricChart';
import { formatDateRange } from '../../DashboardScene/Metrics/useMetric';
import { useOutpostMetricsQuery } from '../../../hooks/useOutpostMetricsQuery';
import { DateRange } from '../../DashboardScene/Metrics/MetricDatePicker';
import { getOutpostTenantDisplayName, useOutpostTenantsById } from '../utils/tenantDisplay';
import { formatDate } from '../../../../utils/date';
import { useDatePreferences } from '../../../hooks/useDatePreferences';
interface SeriesConfig {
  key: string;
  label: string;
  theme_color: 'primary' | 'success' | 'danger';
}
export interface MetricChartConfig {
  title: string;
  chart_type: 'bar' | 'line';
  metric_type: MetricType;
  endpoint: 'events' | 'attempts';
  measures: string[];
  dimensions?: string[];
  value_key: string;
  time_key: string;
  dimension_key?: string;
  tooltip_label: string;
  tenant_id?: string;
  filters?: Record<string, string | string[]>;
  series?: SeriesConfig[];
  datasets_type?: 'stacked' | 'switch';
  height?: number;
  theme_color?: 'primary' | 'success' | 'danger';
  date_range: DateRange;
  refresh_key: number;
}
const mapGranularity = (granularity: string, unit: number): string => {
  const letter = granularity.charAt(0);
  return `${unit}${letter}`;
};
const RATE_MINUTE_THRESHOLD = 2;
export function useMetricChartData(config) {
  const $ = _c(63);
  const {
    timezone
  } = useDatePreferences();
  const {
    title,
    chart_type,
    metric_type,
    endpoint,
    measures,
    dimensions,
    value_key,
    time_key,
    dimension_key,
    tooltip_label,
    tenant_id,
    filters: extra_filters,
    series,
    datasets_type,
    height: t0,
    theme_color: custom_theme_color,
    date_range,
    refresh_key
  } = config;
  const chart_height = t0 === undefined ? 316 : t0;
  const {
    start_date,
    end_date,
    configs: time_unit_configs,
    samples
  } = formatDateRange(new Date(refresh_key), date_range);
  const query = {
    endpoint,
    time: {
      start: start_date.toISOString(),
      end: end_date.toISOString()
    },
    granularity: mapGranularity(time_unit_configs.sampling.granularity, time_unit_configs.sampling.value),
    measures,
    dimensions,
    filters: {
      ...(tenant_id ? {
        tenant_id
      } : {}),
      ...extra_filters
    }
  };
  const {
    raw_data,
    is_loading,
    error
  } = useOutpostMetricsQuery(query);
  const model = endpoint === "events" ? "Events" : "Attempts";
  let t1;
  if ($[0] !== dimension_key || $[1] !== dimensions) {
    t1 = dimensions?.includes("tenant_id") && !!dimension_key?.endsWith(".tenant_id");
    $[0] = dimension_key;
    $[1] = dimensions;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const is_tenant_dimension = t1;
  let t2;
  if ($[3] !== dimension_key || $[4] !== is_tenant_dimension || $[5] !== raw_data) {
    t2 = is_tenant_dimension && dimension_key ? raw_data?.map(datum => datum[dimension_key]).filter(Boolean) || [] : [];
    $[3] = dimension_key;
    $[4] = is_tenant_dimension;
    $[5] = raw_data;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const tenant_ids = t2;
  const {
    tenants_by_id
  } = useOutpostTenantsById(tenant_ids);
  let t3;
  bb0: {
    if (!raw_data && is_loading) {
      t3 = null;
      break bb0;
    }
    const data = raw_data || [];
    if (data.length === 0) {
      let t4;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = [];
        $[7] = t4;
      } else {
        t4 = $[7];
      }
      t3 = t4;
      break bb0;
    }
    if (series?.length) {
      t3 = series.map(s => {
        const series_value_key = `${model}.${s.key}`;
        const chart_data = buildChartData(data, end_date, time_unit_configs, samples, series_value_key, time_key, metric_type, timezone);
        return {
          ...chart_data,
          key: s.key,
          label: s.label,
          theme_color: s.theme_color,
          metric_type,
          getDataLabel: v => {
            const rounded = Math.round(Number(v) * 100) / 100;
            return metric_type === "percent" ? `${rounded}%` : rounded;
          }
        };
      });
      break bb0;
    }
    if (dimension_key && dimensions?.length) {
      let t4;
      if ($[8] !== dimension_key) {
        t4 = (obj, d) => {
          const id = d[dimension_key] || "unknown";
          if (!obj[id]) {
            obj[id] = [];
          }
          obj[id].push(d);
          return obj;
        };
        $[8] = dimension_key;
        $[9] = t4;
      } else {
        t4 = $[9];
      }
      const data_by_dimension = data.reduce(t4, {} as Record<string, Record<string, any>[]>);
      t3 = Object.entries(data_by_dimension).map(t5 => {
        const [key, values] = t5;
        const chart_data_0 = buildChartData(values, end_date, time_unit_configs, samples, value_key, time_key, metric_type, timezone);
        return {
          ...chart_data_0,
          key,
          label: is_tenant_dimension ? getOutpostTenantDisplayName(tenants_by_id[key]) || key : key,
          hex_color: generateRandomChartColor(key),
          metric_type,
          getDataLabel: v_0 => {
            const rounded_0 = Math.round(Number(v_0) * 100) / 100;
            return metric_type === "percent" ? `${rounded_0}%` : rounded_0;
          }
        };
      });
      break bb0;
    }
    const chart_data_1 = buildChartData(data, end_date, time_unit_configs, samples, value_key, time_key, metric_type, timezone);
    const t4 = custom_theme_color || "primary";
    let t5;
    if ($[10] !== metric_type) {
      t5 = v_1 => {
        const rounded_1 = Math.round(Number(v_1) * 100) / 100;
        return metric_type === "percent" ? `${rounded_1}%` : rounded_1;
      };
      $[10] = metric_type;
      $[11] = t5;
    } else {
      t5 = $[11];
    }
    let t6;
    if ($[12] !== chart_data_1 || $[13] !== endpoint || $[14] !== metric_type || $[15] !== t4 || $[16] !== t5 || $[17] !== tooltip_label) {
      t6 = [{
        ...chart_data_1,
        key: endpoint,
        label: tooltip_label,
        theme_color: t4,
        metric_type,
        getDataLabel: t5
      }];
      $[12] = chart_data_1;
      $[13] = endpoint;
      $[14] = metric_type;
      $[15] = t4;
      $[16] = t5;
      $[17] = tooltip_label;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    t3 = t6;
  }
  const datasets = t3;
  let t4;
  bb1: {
    if (!datasets || metric_type !== "rate") {
      let t5;
      if ($[19] !== datasets) {
        t5 = {
          scaled_datasets: datasets,
          rate_as: null
        };
        $[19] = datasets;
        $[20] = t5;
      } else {
        t5 = $[20];
      }
      t4 = t5;
      break bb1;
    }
    const highest = datasets.reduce(_temp, 0);
    const computed_rate_as = highest < RATE_MINUTE_THRESHOLD ? "minute" : "second";
    let t5;
    if ($[21] !== computed_rate_as) {
      t5 = v_2 => default_data_formatter.rate(v_2, computed_rate_as);
      $[21] = computed_rate_as;
      $[22] = t5;
    } else {
      t5 = $[22];
    }
    const getDataLabel = t5;
    if (computed_rate_as === "minute") {
      let t6;
      if ($[23] !== datasets || $[24] !== getDataLabel) {
        let t7;
        if ($[26] !== getDataLabel) {
          t7 = d_1 => ({
            ...d_1,
            data: d_1.data.map(_temp2),
            total: d_1.total * 60,
            highest: d_1.highest * 60,
            average: d_1.average * 60,
            getDataLabel
          });
          $[26] = getDataLabel;
          $[27] = t7;
        } else {
          t7 = $[27];
        }
        t6 = datasets.map(t7);
        $[23] = datasets;
        $[24] = getDataLabel;
        $[25] = t6;
      } else {
        t6 = $[25];
      }
      let t7;
      if ($[28] !== computed_rate_as || $[29] !== t6) {
        t7 = {
          scaled_datasets: t6,
          rate_as: computed_rate_as
        };
        $[28] = computed_rate_as;
        $[29] = t6;
        $[30] = t7;
      } else {
        t7 = $[30];
      }
      t4 = t7;
      break bb1;
    }
    let t6;
    if ($[31] !== datasets || $[32] !== getDataLabel) {
      let t7;
      if ($[34] !== getDataLabel) {
        t7 = d_2 => ({
          ...d_2,
          getDataLabel
        });
        $[34] = getDataLabel;
        $[35] = t7;
      } else {
        t7 = $[35];
      }
      t6 = datasets.map(t7);
      $[31] = datasets;
      $[32] = getDataLabel;
      $[33] = t6;
    } else {
      t6 = $[33];
    }
    let t7;
    if ($[36] !== computed_rate_as || $[37] !== t6) {
      t7 = {
        scaled_datasets: t6,
        rate_as: computed_rate_as
      };
      $[36] = computed_rate_as;
      $[37] = t6;
      $[38] = t7;
    } else {
      t7 = $[38];
    }
    t4 = t7;
  }
  const {
    scaled_datasets,
    rate_as
  } = t4;
  let t5;
  bb2: {
    if (!scaled_datasets) {
      let t6;
      if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = [undefined, undefined];
        $[39] = t6;
      } else {
        t6 = $[39];
      }
      t5 = t6;
      break bb2;
    }
    if (dimensions?.length) {
      let t6;
      if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = [undefined, undefined];
        $[40] = t6;
      } else {
        t6 = $[40];
      }
      t5 = t6;
      break bb2;
    }
    if (metric_type === "count") {
      let t6;
      if ($[41] !== scaled_datasets) {
        const sum = scaled_datasets.reduce(_temp3, 0);
        t6 = Math.round(sum);
        $[41] = scaled_datasets;
        $[42] = t6;
      } else {
        t6 = $[42];
      }
      let t7;
      if ($[43] !== t6) {
        t7 = [t6, undefined];
        $[43] = t6;
        $[44] = t7;
      } else {
        t7 = $[44];
      }
      t5 = t7;
      break bb2;
    }
    if (metric_type === "rate") {
      let t6;
      if ($[45] !== rate_as || $[46] !== scaled_datasets) {
        const avg = scaled_datasets.reduce(_temp4, 0);
        t6 = default_data_formatter.rate(avg, rate_as || undefined);
        $[45] = rate_as;
        $[46] = scaled_datasets;
        $[47] = t6;
      } else {
        t6 = $[47];
      }
      const t7 = `per ${rate_as}`;
      let t8;
      if ($[48] !== t6 || $[49] !== t7) {
        t8 = [t6, t7];
        $[48] = t6;
        $[49] = t7;
        $[50] = t8;
      } else {
        t8 = $[50];
      }
      t5 = t8;
      break bb2;
    }
    let t6;
    if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = [undefined, undefined];
      $[51] = t6;
    } else {
      t6 = $[51];
    }
    t5 = t6;
  }
  const [total, total_hint] = t5;
  const t6 = !!error;
  const t7 = !!dimensions?.length || !!series?.length;
  let t8;
  if ($[52] !== chart_height || $[53] !== chart_type || $[54] !== datasets_type || $[55] !== is_loading || $[56] !== scaled_datasets || $[57] !== t6 || $[58] !== t7 || $[59] !== title || $[60] !== total || $[61] !== total_hint) {
    t8 = {
      chart_type,
      datasets: scaled_datasets,
      datasets_type,
      title,
      loading: is_loading,
      error: t6,
      total,
      total_hint,
      height: chart_height,
      force_legend: t7
    };
    $[52] = chart_height;
    $[53] = chart_type;
    $[54] = datasets_type;
    $[55] = is_loading;
    $[56] = scaled_datasets;
    $[57] = t6;
    $[58] = t7;
    $[59] = title;
    $[60] = total;
    $[61] = total_hint;
    $[62] = t8;
  } else {
    t8 = $[62];
  }
  return t8;
}
function _temp4(acc_1, d_4) {
  return acc_1 + d_4.average;
}
function _temp3(acc_0, d_3) {
  return acc_0 + d_3.total;
}
function _temp2(point) {
  return {
    ...point,
    y: (point.y as number) * 60
  };
}
function _temp(acc, d_0) {
  return Math.max(acc, d_0.highest);
}
const MetricChart: React.FC<MetricChartConfig> = config => {
  const $ = _c(2);
  const data = useMetricChartData(config);
  let t0;
  if ($[0] !== data) {
    t0 = <MetricChartBase {...data} />;
    $[0] = data;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export default MetricChart;
export function buildChartData(values: Record<string, any>[], end_date: Date, time_unit_configs: any, samples: number, value_key: string, time_key: string, metric_type: MetricType, timezone = 'UTC') {
  const result = {
    data: [] as ChartDataSet['data'],
    total: 0,
    highest: 0,
    average: 0
  };
  let valid_count = 0;
  const [priority_display_method, priority_display_values] = time_unit_configs.sampling.priority_display;
  for (let i = 0; i < samples; i++) {
    const step_start = sub(end_date, {
      [`${time_unit_configs.sampling.granularity}s`]: time_unit_configs.sampling.value * (samples - i)
    });
    const step_end = add(step_start, {
      [`${time_unit_configs.sampling.granularity}s`]: time_unit_configs.sampling.value
    });
    const time_frame_values = values.filter(d => {
      const t = addMinutes(new Date(d[time_key]), 1).getTime();
      return t < step_end.getTime() && t >= step_start.getTime();
    });
    const time_frame_count = time_frame_values.reduce((count, d) => {
      if (!d[value_key]) return count;
      return count + parseFloat(d[value_key]);
    }, 0);
    const priority_display_value = priority_display_method(step_end);
    let y = time_frame_count;
    if (metric_type === 'rate') {
      y = time_frame_count / differenceInSeconds(step_end, step_start);
    } else if (metric_type === 'percent') {
      // API returns 0.0-1.0, display as 0-100%
      const count = time_frame_values.length;
      y = count > 0 ? time_frame_count / count * 100 : 0;
    }
    result.data.push({
      priority_display: priority_display_values.some((value: number) => value === priority_display_value),
      sample_range: {
        min: step_start.toISOString(),
        max: step_end.toISOString()
      },
      x: formatDate(step_end, time_unit_configs.sampling.date_format, timezone),
      y
    });
    if (!isNaN(y)) {
      valid_count++;
      result.total += y;
      result.highest = Math.max(result.highest, y);
      result.average = result.total / valid_count;
    }
  }
  return result;
}